import { useTranslation } from "react-i18next";
import type { Route } from "./+types/_index";
import { Container } from "~/components/layout/Container";
import { PageHeader } from "~/components/layout/PageHeader";
import { MerchantDirectory } from "~/components/merchant/MerchantDirectory";
import {
  getMerchantClassifications,
  getMerchantCountries,
  getMerchants,
} from "~/lib/api/merchants.server";
import {
  getMerchantCategories,
  getMerchantCategoryRelationships,
} from "~/lib/api/categories.server";
import { safe } from "~/lib/safe";
import { cacheHeaders } from "~/lib/cache-headers";

export async function loader() {
  const [
    merchants,
    merchantCountries,
    categories,
    categoryRelationships,
    classifications,
  ] = await Promise.all([
    safe(() => getMerchants(), [] as Awaited<ReturnType<typeof getMerchants>>),
    safe(
      () => getMerchantCountries(),
      [] as Awaited<ReturnType<typeof getMerchantCountries>>,
    ),
    safe(
      () => getMerchantCategories(),
      [] as Awaited<ReturnType<typeof getMerchantCategories>>,
    ),
    safe(
      () => getMerchantCategoryRelationships(),
      [] as Awaited<ReturnType<typeof getMerchantCategoryRelationships>>,
    ),
    safe(
      () => getMerchantClassifications(),
      [] as Awaited<ReturnType<typeof getMerchantClassifications>>,
    ),
  ]);
  return {
    merchants,
    merchantCountries,
    categories,
    categoryRelationships,
    classifications,
  };
}

export const headers = () => cacheHeaders["public-300"];

export const meta: Route.MetaFunction = () => [
  { title: "Merchant Directory" },
  {
    name: "description",
    content:
      "Browse verified merchants and trusted service providers on the Raytio Trust Marketplace.",
  },
];

export default function MerchantsRoute({ loaderData }: Route.ComponentProps) {
  const {
    merchants,
    merchantCountries,
    categories,
    categoryRelationships,
    classifications,
  } = loaderData;
  const { t } = useTranslation();
  return (
    <>
      <PageHeader
        eyebrow={t("MerchantsPage.eyebrow")}
        title={t("MerchantsPage.title")}
        subtitle={t("MerchantsPage.subtitle")}
      />
      <section className="py-12">
        <Container>
          <MerchantDirectory
            merchants={merchants}
            merchantCountries={merchantCountries}
            categories={categories}
            categoryRelationships={categoryRelationships}
            classifications={classifications}
          />
        </Container>
      </section>
    </>
  );
}
